import React, { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { FeeComponent } from "../../types/fee";

interface FeeStructureEditorValue {
  feeComponents: FeeComponent[];
  billingFrequency?: string;
  perPeriodAmount?: number | null;
  createInvoiceOnEnrollment?: boolean;
  isActive?: boolean;
  description?: string;
}

interface FeeStructureEditorProps {
  value?: FeeStructureEditorValue;
  onChange: (value: FeeStructureEditorValue) => void;
}

const defaultComponent = (): FeeComponent => ({
  name: "Tuition Fee",
  amount: 0,
  category: "tuition",
  description: "",
  isOptional: false,
});

const FeeStructureEditor: React.FC<FeeStructureEditorProps> = ({
  value,
  onChange,
}) => {
  const [feeComponents, setFeeComponents] = useState<FeeComponent[]>(
    value?.feeComponents && value?.feeComponents.length > 0
      ? value!.feeComponents
      : [defaultComponent()]
  );
  const [billingFrequency, setBillingFrequency] = useState<string>(
    value?.billingFrequency || "monthly"
  );
  const [perPeriodAmount, setPerPeriodAmount] = useState<number | null>(
    value?.perPeriodAmount ?? null
  );
  const [createInvoiceOnEnrollment, setCreateInvoiceOnEnrollment] =
    useState<boolean>(value?.createInvoiceOnEnrollment || false);
  const [isActive, setIsActive] = useState<boolean>(
    value?.isActive ?? true
  );
  const [description, setDescription] = useState<string>(value?.description || "");

  useEffect(() => {
    onChange({
      feeComponents,
      billingFrequency,
      perPeriodAmount,
      createInvoiceOnEnrollment,
      isActive,
      description,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feeComponents, billingFrequency, perPeriodAmount, createInvoiceOnEnrollment, isActive, description]);

  const addComponent = () => {
    setFeeComponents((prev) => [...prev, defaultComponent()]);
  };

  const updateComponent = (index: number, field: keyof FeeComponent, value: any) => {
    const updated = [...feeComponents];
    updated[index] = { ...updated[index], [field]: value } as FeeComponent;
    setFeeComponents(updated);
  };

  const removeComponent = (index: number) => {
    if (feeComponents.length <= 1) return;
    setFeeComponents((prev) => prev.filter((_, i) => i !== index));
  };

  const totalAmount = feeComponents.reduce((sum, c) => sum + (c.amount || 0), 0);

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Billing Frequency</label>
          <select
            value={billingFrequency}
            onChange={(e) => setBillingFrequency(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="monthly">Monthly</option>
            <option value="weekly">Weekly</option>
            <option value="quarterly">Quarterly</option>
            <option value="annual">Annual</option>
            <option value="term">Per Term</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Per-period Amount (optional)</label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={perPeriodAmount ?? ""}
            onChange={(e) => setPerPeriodAmount(e.target.value === "" ? null : parseFloat(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Leave empty to use total of components"
          />
        </div>

        <div className="flex items-center">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={createInvoiceOnEnrollment} onChange={(e) => setCreateInvoiceOnEnrollment(e.target.checked)} />
            <span className="text-sm">Create invoice on enrollment</span>
          </label>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Fee Components</label>
        <div className="space-y-4">
          {feeComponents.map((component, index) => (
            <div key={index} className="p-4 border border-gray-200 rounded-lg bg-gray-50">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Component Name *</label>
                  <input
                    type="text"
                    value={component.name}
                    onChange={(e) => updateComponent(index, "name", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="e.g., Tuition Fee"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={component.amount}
                    onChange={(e) => updateComponent(index, "amount", parseFloat(e.target.value || "0"))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="0.00"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select value={component.category} onChange={(e) => updateComponent(index, "category", e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md">
                    <option value="tuition">Tuition</option>
                    <option value="books">Books & Materials</option>
                    <option value="uniform">Uniform</option>
                    <option value="transport">Transport</option>
                    <option value="exam">Examination</option>
                    <option value="activity">Activity</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input type="text" value={component.description || ""} onChange={(e) => updateComponent(index, "description", e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md" placeholder="Optional description" />
              </div>

              <div className="mt-4 flex items-center justify-between">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={component.isOptional || false} onChange={(e) => updateComponent(index, "isOptional", e.target.checked)} />
                  <span className="ml-2 text-sm">Optional</span>
                </label>

                {feeComponents.length > 1 && (
                  <button type="button" onClick={() => removeComponent(index)} className="text-red-600 hover:text-red-800 flex items-center gap-2">
                    <Trash2 className="w-4 h-4" />
                    Remove
                  </button>
                )}
              </div>
            </div>
          ))}

          <div>
            <button type="button" onClick={addComponent} className="btn btn-outline btn-sm flex items-center gap-2">
              <Plus className="w-4 h-4" /> Add Fee Component
            </button>
          </div>

          {feeComponents.some((comp) => comp.amount > 0) && (
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Total Amount:</span>
                <span className="text-lg font-bold text-blue-600">Ksh. {totalAmount.toFixed(2)}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="mt-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
        <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md" placeholder="Optional description for this fee structure" />
      </div>

      <div className="mt-4 flex items-center gap-2">
        <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
        <label className="text-sm">Active fee structure</label>
      </div>
    </div>
  );
};

export default FeeStructureEditor;
